/* eslint-disable react/jsx-curly-newline */
import React, { useCallback, useMemo } from 'react';
import {
  Box,
  CalendarPickerIcon,
  Controller,
  ControllerWrapper,
  DateRangeDropdown,
  FieldTypes,
  FieldWrapper,
  FormFieldsContainer,
  Label,
  Option,
  RegularField,
  Text,
  ToggleButtonGroup,
  useFormContext,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import { ICreateDocumentDtoDocumentTypeEnum } from '@/types';

import {
  DESCRIPTION_MAX_LENGTH,
  LettersOfMedicalNecessityForm,
  LettersOfMedicalNecessityFormField,
} from '../../hooks/useLettersOfMedicalNecessityForm';
import { LMNPopupMode } from '../../LettersOfMedicalNecessity.types';
const TYPE_OPTIONS: Option[] = [
  {
    key: ICreateDocumentDtoDocumentTypeEnum.LETTER_OF_MEDICAL_NECESSITY,
    title: 'Letter of Medical Necessity',
  },
];

interface Props {
  mode: LMNPopupMode;
  createForClaimId?: string;
}

const DocumentPreviewPopupForm: React.FC<Props> = ({ mode, createForClaimId }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<LettersOfMedicalNecessityForm>();
  const { isMobile } = useResponsive();
  const isEditMode = useMemo(() => {
    return (
      mode === LMNPopupMode.EDIT ||
      mode === LMNPopupMode.UPLOAD ||
      mode === LMNPopupMode.PARTIAL_EDIT ||
      mode === LMNPopupMode.CONVERT
    );
  }, [mode]);

  const getFormattedDates = useCallback((dates: Date[]) => {
    if (!dates || !dates.length) {
      return EMPTY_FIELD_VALUE;
    }
    let text = '';
    if (dates[0]) {
      text += dayjs(dates[0]).format(DEFAULT_DATE_FORMAT);
    }
    if (dates[0] && dates[1]) {
      text += ' - ';
    }
    if (dates[1]) {
      text += dayjs(dates[1]).format(DEFAULT_DATE_FORMAT);
    }
    return text;
  }, []);

  return (
    <Box
      pad={isMobile ? 'spacing16' : 'spacing24'}
      background="canvas"
      round="container1Round"
    >
      <FormFieldsContainer>
        {mode === LMNPopupMode.PARTIAL_EDIT || mode === LMNPopupMode.CONVERT || createForClaimId ? null : (
          <FieldWrapper>
            <Label
              name={LettersOfMedicalNecessityFormField.type}
              isNarrow
              showRequireIcon={mode === LMNPopupMode.UPLOAD}
            >
              Type
            </Label>
            <ControllerWrapper>
              <Controller
                name={LettersOfMedicalNecessityFormField.type}
                control={control}
                render={({ field }) =>
                  isEditMode ? (
                    <Box width="100%">
                      <ToggleButtonGroup
                        testId="lmn_type"
                        value={field.value}
                        options={TYPE_OPTIONS}
                        onChange={field.onChange}
                        columns={['auto']}
                      />
                    </Box>
                  ) : (
                    <Text>
                      {TYPE_OPTIONS?.find((value) => value?.key === field.value)?.title || EMPTY_FIELD_VALUE}
                    </Text>
                  )
                }
              />
            </ControllerWrapper>
          </FieldWrapper>
        )}
        <FieldWrapper>
          <Label
            name={LettersOfMedicalNecessityFormField.name}
            isNarrow
            showRequireIcon={mode !== LMNPopupMode.VIEW}
          >
            Name
          </Label>

          <ControllerWrapper errorText={errors?.[LettersOfMedicalNecessityFormField.name]?.message}>
            <Controller
              name={LettersOfMedicalNecessityFormField.name}
              control={control}
              render={({ field }) =>
                mode === LMNPopupMode.VIEW ? (
                  <Text>{field.value}</Text>
                ) : (
                  <RegularField
                    testId="lmn_name"
                    name={LettersOfMedicalNecessityFormField.name}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Name"
                    isError={!!errors?.[LettersOfMedicalNecessityFormField.name]?.message}
                    editMode={
                      mode === LMNPopupMode.UPLOAD ||
                      mode === LMNPopupMode.EDIT ||
                      mode === LMNPopupMode.PARTIAL_EDIT ||
                      mode === LMNPopupMode.CONVERT
                    }
                  />
                )
              }
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper align="start" fill>
          <Label name={LettersOfMedicalNecessityFormField.description} isNarrow showRequireIcon={false}>
            Description
          </Label>
          <ControllerWrapper
            errorText={errors?.[LettersOfMedicalNecessityFormField.description]?.message}
            style={{
              width: '100%',
            }}
          >
            <Controller
              name={LettersOfMedicalNecessityFormField.description}
              control={control}
              render={({ field }) =>
                mode === LMNPopupMode.VIEW ? (
                  <Text>{field.value}</Text>
                ) : (
                  <RegularField
                    testId="lmn_description"
                    name={LettersOfMedicalNecessityFormField.description}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Description"
                    inputType={FieldTypes.TextArea}
                    isError={!!errors?.[LettersOfMedicalNecessityFormField.description]?.message}
                    editMode={isEditMode}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                )
              }
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper align="center">
          <Label name={LettersOfMedicalNecessityFormField.validDates} isNarrow>
            Valid dates
          </Label>
          <ControllerWrapper>
            <Controller
              name={LettersOfMedicalNecessityFormField.validDates}
              control={control}
              render={({ field }) => {
                if (isEditMode) {
                  return (
                    <DateRangeDropdown
                      isTwoCalendars
                      isInputMode
                      ranges={null}
                      placeholder="Select dates"
                      onChange={(val) => {
                        field.onChange(val);
                      }}
                      startDate={field.value?.[0] as unknown as Date}
                      endDate={field.value?.[1] as unknown as Date}
                      icon={<CalendarPickerIcon color="iconPrimary" />}
                    />
                  );
                }
                return <Text>{getFormattedDates(field.value)}</Text>;
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>
      </FormFieldsContainer>
    </Box>
  );
};

export default DocumentPreviewPopupForm;
